import React, { useEffect } from 'react';
import { Configuration } from '../../data/configuration.js';
import { SectionPanel } from '../view-content.js';
import { CloudData } from '../../data/cloud-data.js';

//-----------------------------------------------------------------------------------------
// Type Definitions:
//-----------------------------------------------------------------------------------------

interface SummaryStepOneProps {
	totalCost: (price: number, invalidText?: string) => string;
}

type RowItem = { name: string; option_code: string; price: string | number; value: string; }

type SummaryGroup = { group: string; items: RowItem[]; }

type Summary = SummaryGroup[];

interface ItemisedPriceBreakdownProps {
	items: RowItem[];
	totalCost: (price: number, invalidText?: string) => string;
}

//-----------------------------------------------------------------------------------------
// Public Components:
//-----------------------------------------------------------------------------------------

export default function SummaryStepOne(props: SummaryStepOneProps) {

	// states.
	const [summary, setSummary] = React.useState<Summary>([]);

	// generate a summary on page render.
	useEffect(() => {

		// generate a summary of the selected options.
		const generateSummary = async () => {
			const generatedSummary = await CloudData.generateSummary();
			return generatedSummary as unknown as Summary;
		};

		// set the summary in the promise callback, not the effect body, to avoid a synchronous setState.
		generateSummary()
			.then(setSummary)
			.catch(err => console.error('failed to generate summary: ', err));
	}, []);

	const totalTransmission = props.totalCost(Configuration.costBreakdown.transmission);
	const totalPaintwork = props.totalCost(Configuration.costBreakdown.paintwork);
	const totalWheels = props.totalCost(Configuration.costBreakdown.wheels);
	const totalInterior = props.totalCost(Configuration.costBreakdown.interior);
	const totalCockpit = props.totalCost(Configuration.costBreakdown.cockpit);
	const totalExterior = props.totalCost(Configuration.costBreakdown.exterior);
	const totalAccessories = props.totalCost(Configuration.costBreakdown.accessories);

	// map the cost of each section to its name.
	const nameToCostMap = {
		gearbox: totalTransmission,
		paintwork: totalPaintwork,
		wheels: totalWheels,
		interior: totalInterior,
		cockpit: totalCockpit,
		exterior: totalExterior,
		accessories: totalAccessories
	}

	// return the HTML.
	return (
		<div className="price-breakdown-sections">
			<div>
				{summary.length > 0 && summary.map((summaryGroup: SummaryGroup) => {
					// clean the group name so it can work as a key.
					const costKey = summaryGroup.group.toLowerCase().split(' ')[0];
					const total = nameToCostMap[costKey as keyof typeof nameToCostMap];

					// return the HTML.
					return (
						<SectionPanel
							key={summaryGroup.group}
							border={true}
							shouldStartClosed={true}
							arrowColour="white"
							subHeading={`${summaryGroup.group}${total ? ': ' + total : ''}`}
						>
							<ItemisedPriceBreakdown
								totalCost={props.totalCost}
								items={summaryGroup.items}
							/>
						</SectionPanel>
					);
				})}
			</div>
			<div className="small-print">
				<p>*Some aftermarket accessories are not homologated for all markets and may be subject to additional fitting and delivery charges.</p>
				<p>Please consult your Morgan Dealer for further information</p>
			</div>
		</div>
	);
}

//-----------------------------------------------------------------------------------------
// Private Components:
//-----------------------------------------------------------------------------------------

function ItemisedPriceBreakdown(props: ItemisedPriceBreakdownProps) {

	return (
		<div>
			{props.items.map((item: RowItem, index) => {
				const price = sanitisePrice(item.price);

				let name = item.name;
				let value = item.value;

				if (!name) {
					name = value;
					value = 'Selected';
				}

				return (
					<div key={`${item.option_code}-${index}`} className="item-breakdown-row">
						<p>{name}</p>
						<p id="item-value">{value}</p>
						<p className="price">{props.totalCost(price)}</p>
					</div>
				);
			})}
		</div>
	);
}

//-----------------------------------------------------------------------------------------
// Private Functions:
//-----------------------------------------------------------------------------------------

// some prices are passed in as '£1.00' and others are passed in as 1.00, this function sanitises them to be the same.
function sanitisePrice(price: string | number) {
	if (typeof price === 'string') {
		return parseFloat(price.replace(/[^0-9.]/g, ''));
	}
	return price;
};